import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";
import { ArrowRight, Zap, PackageCheck, Sparkles, ArrowUpRight, MapPin, Phone, Building2, Home as HomeIcon, Stethoscope } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { AREAS } from "@/data/areas";
import { TOWN_PAGE_SLUGS } from "@/data/townPages";

const HERO = {
  src: "/images/smart-cooler-hero.800w.d28942cc.webp",
  srcSet: "/images/smart-cooler-hero.800w.d28942cc.webp 800w, /images/smart-cooler-hero.1600w.b38ec30e.webp 1600w",
  width: 800,
  height: 1066,
};
const INTERIOR = {
  src: "/images/smart-cooler-interior.600w.149051d1.webp",
  srcSet: "/images/smart-cooler-interior.600w.149051d1.webp 600w, /images/smart-cooler-interior.820w.3e09ab96.webp 820w",
  width: 600,
  height: 849,
};
const LOUNGE = {
  src: "/images/smart-cooler-lounge.500w.c96b67a5.webp",
  srcSet: "/images/smart-cooler-lounge.500w.c96b67a5.webp 500w, /images/smart-cooler-lounge.1000w.9fe53323.webp 1000w",
  width: 500,
  height: 543,
};

const TiltCard = ({ children, className = "" }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useTransform(my, [0, 1], [5, -5]);
  const rotateY = useTransform(mx, [0, 1], [-5, 5]);
  return (
    <motion.div
      className={className}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 1000 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => { mx.set(0.5); my.set(0.5); }}
    >
      {children}
    </motion.div>
  );
};

const STANDARDS = [
  {
    num: "01",
    title: "Fast Communication",
    icon: Zap,
    body: "Questions, restock requests, issues — a real person replies within one business day. Call, email, or scan the QR code on the machine.",
  },
  {
    num: "02",
    title: "Always Stocked",
    icon: PackageCheck,
    body: "Smart telemetry tells us what is selling before it runs out. Your favorites get replenished on a schedule built around your location.",
  },
  {
    num: "03",
    title: "Spotless & Clean",
    icon: Sparkles,
    body: "Every visit includes a full wipe-down and freshness check. A clean machine is not a bonus for us — it is the baseline.",
  },
];

const AUDIENCES = [
  { to: "/office-vending", icon: Building2, title: "Offices", body: "Break-room drinks and snacks steps from every desk, so your team stays on-site and refreshed." },
  { to: "/apartment-building-vending", icon: HomeIcon, title: "Apartment Buildings", body: "A 24/7 resident amenity for lobbies, gyms, and mailrooms, with no staffing burden on management." },
  { to: "/medical-office-vending", icon: Stethoscope, title: "Medical Offices", body: "Drinks and snacks for staff on long shifts and families in waiting rooms, kept spotless." },
];

const Home = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-36 pb-20 lg:pt-48 lg:pb-32">
        <motion.div
          style={{ y: glowY }}
          aria-hidden="true"
          className="absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-vortex-blue/20 blur-[140px] animate-pulse-glow"
        />
        <div aria-hidden="true" className="absolute top-1/2 -left-52 h-[28rem] w-[28rem] rounded-full bg-vortex-cyan/10 blur-[120px]" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.1}>
            <Overline data-testid="hero-overline">Vortex Vending Services LLC</Overline>
          </MaskedLine>
          <h1 className="mt-8 font-heading font-black tracking-tighter leading-[0.98] text-4xl sm:text-6xl lg:text-[4.2rem]" data-testid="hero-heading">
            <MaskedLine delay={0.25}>SMART COOLER VENDING FOR</MaskedLine>
            <MaskedLine delay={0.4}>OFFICES, APARTMENTS &</MaskedLine>
            <MaskedLine delay={0.55}>
              MEDICAL BUILDINGS IN <span className="text-vortex-cyan drop-shadow-[0_0_40px_rgba(92,179,226,0.35)]">WEST CHESTER, PA</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.7}>
            <p className="mt-7 font-mono text-sm tracking-[0.3em] text-vortex-cyan uppercase" data-testid="hero-tagline">
              Modern Vending for a Modern World
            </p>
          </MaskedLine>

          <div className="mt-14 grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal delay={0.7}>
                <p className="max-w-md text-base leading-relaxed text-slate-400 sm:text-lg">
                  Smart coolers, stocked and spotless, backed by communication so fast
                  it feels instant. Vending that finally keeps up with your workplace.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <button
                    data-testid="hero-explore-button"
                    onClick={() => document.getElementById("smart-cooler")?.scrollIntoView({ behavior: "smooth" })}
                    className="group flex items-center gap-3 rounded-full bg-vortex-cyan px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(92,179,226,0.5)]"
                  >
                    Explore the Smart Cooler
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <a
                    data-testid="hero-call-link"
                    href="tel:+16109838060"
                    className="flex items-center gap-3 rounded-full border border-white/15 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-slate-300 transition-all duration-300 hover:border-vortex-cyan/60 hover:text-vortex-cyan"
                  >
                    <Phone size={15} />
                    Call (610) 983-8060
                  </a>
                </div>
                <div className="mt-14 grid grid-cols-3 gap-6 border-t border-white/10 pt-8" data-testid="hero-stats">
                  {[
                    ["1 Day", "Reply standard"],
                    [String(AREAS.length), "Towns served"],
                    ["Cashless", "Card & mobile pay"],
                  ].map(([stat, label]) => (
                    <div key={label}>
                      <p className="font-heading text-2xl font-bold text-white sm:text-3xl">{stat}</p>
                      <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">{label}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.85}>
                <div className="relative">
                  <div className="clip-frame relative overflow-hidden border border-white/10 glow-cyan" data-testid="hero-image-frame">
                    <motion.img
                      style={{ y: imgY, scale: 1.15 }}
                      src={HERO.src}
                      srcSet={HERO.srcSet}
                      sizes="(min-width: 1024px) 58vw, 100vw"
                      width={HERO.width}
                      height={HERO.height}
                      fetchPriority="high"
                      alt="Vortex smart cooler stocked with drinks and snacks"
                      className="h-[26rem] w-full object-cover lg:h-[34rem]"
                      data-testid="hero-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-vortex-ink/80 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                      <p className="font-mono text-[10px] tracking-[0.25em] text-vortex-cyan uppercase">
                        Smart Cooler
                      </p>
                      <p className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                        Cashless Checkout
                      </p>
                    </div>
                  </div>
                  <div aria-hidden="true" className="absolute -bottom-4 -right-4 h-full w-full border border-vortex-cyan/20 clip-frame -z-10" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* PRODUCT SHOWCASE */}
      <section id="smart-cooler" className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="product-section">
        <Reveal>
          <Overline>01 — The Lineup</Overline>
          <h2 className="mt-6 max-w-3xl font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            One machine. Zero compromises.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-8" delay={0.1}>
            <TiltCard className="group relative h-full overflow-hidden border border-white/10 bg-vortex-surface/60 backdrop-blur-xl transition-colors duration-500 hover:border-vortex-cyan/40" >
              <div className="grid h-full grid-cols-1 md:grid-cols-2">
                <div className="relative min-h-[20rem] overflow-hidden">
                  <img
                    src={INTERIOR.src}
                    srcSet={INTERIOR.srcSet}
                    sizes="(min-width: 1024px) 30vw, 100vw"
                    width={INTERIOR.width}
                    height={INTERIOR.height}
                    loading="lazy"
                    alt="Smart cooler shelves stocked with soda, water, chips, and candy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    data-testid="cooler-card-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-vortex-surface/60" />
                </div>
                <div className="flex flex-col justify-between p-10 lg:p-12">
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.25em] text-vortex-cyan uppercase">Now Deploying</p>
                    <h3 className="mt-4 font-heading text-3xl font-bold tracking-tight text-white lg:text-4xl">
                      The Smart Cooler
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-slate-400">
                      Grab-and-go soda, water, sports drinks, chips, candy, and healthy
                      snacks with cashless checkout and live inventory. It knows what is
                      inside, so we know when to restock.
                    </p>
                  </div>
                  <ul className="mt-8 space-y-3" data-testid="cooler-feature-list">
                    {["Cashless & mobile payments", "Real-time inventory telemetry", "Sodas, water, Gatorade & more", "Chips, candy & healthy snacks", "Remote temperature monitoring"].map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-slate-300">
                        <span className="h-1 w-1 rounded-full bg-vortex-cyan" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </TiltCard>
          </Reveal>

          <Reveal className="lg:col-span-4" delay={0.2}>
            <div className="flex h-full flex-col justify-between border border-dashed border-white/15 p-10 transition-colors duration-500 hover:border-vortex-cyan/40 lg:p-12" data-testid="coming-soon-card">
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">02 — On the Horizon</p>
                <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-slate-300 lg:text-3xl">
                  More formats in development
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-500">
                  Smart Coolers are just the beginning. New vending options are on our
                  roadmap — and customer feedback helps decide what comes next.
                </p>
              </div>
              <button
                data-testid="coming-soon-feedback-button"
                onClick={() => navigate("/feedback")}
                className="group mt-10 flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-vortex-cyan"
              >
                Shape the roadmap
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE VORTEX STANDARD */}
      <section className="relative border-t border-white/5 bg-vortex-surface/20" data-testid="standards-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <Overline>02 — The Vortex Standard</Overline>
                <h2 className="mt-6 font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">
                  What we specialize in.
                </h2>
                <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-400">
                  Three promises, kept at every location, every visit. This is what
                  "modern vending" actually means to us.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="clip-frame mt-12 hidden overflow-hidden border border-white/10 lg:block">
                  <img src={LOUNGE.src} srcSet={LOUNGE.srcSet} sizes="(min-width: 1024px) 33vw, 100vw" width={LOUNGE.width} height={LOUNGE.height} loading="lazy" alt="Vortex smart cooler studio product shot" className="h-72 w-full object-cover" data-testid="standards-image" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              {STANDARDS.map((s, i) => (
                <Reveal key={s.num} delay={i * 0.1}>
                  <div
                    className="group flex flex-col gap-6 border-t border-white/10 py-12 transition-colors duration-500 hover:border-vortex-cyan/30 sm:flex-row sm:items-start sm:gap-12"
                    data-testid={`standard-row-${s.num}`}
                  >
                    <span className="font-mono text-sm tracking-[0.25em] text-vortex-cyan">{s.num}</span>
                    <div className="flex-1">
                      <h3 className="flex items-center gap-4 font-heading text-2xl font-bold tracking-tight text-white transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">
                        <s.icon size={26} className="text-vortex-cyan" />
                        {s.title}
                      </h3>
                      <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400">{s.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="border-t border-white/5" data-testid="audiences-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <Overline>03 — Who We Serve</Overline>
            <h2 className="mt-6 font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">
              Built for where people work and live.
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-400">
              There is no cost to your location. We provide, install, stock, and
              maintain the cooler; people pay only for what they buy.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.to} delay={0.08 * i}>
                <Link
                  to={a.to}
                  data-testid={`home-audience-${a.to.slice(1)}`}
                  className="group flex h-full flex-col justify-between border border-white/10 p-8 transition-colors duration-500 hover:border-vortex-cyan/40 lg:p-10"
                >
                  <div>
                    <a.icon size={26} className="text-vortex-cyan" />
                    <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-white">{a.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{a.body}</p>
                  </div>
                  <span className="mt-8 flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-vortex-cyan">
                    Learn more
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section className="border-t border-white/5" data-testid="areas-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <Overline>04 — Coverage</Overline>
            <h2 className="mt-6 font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">
              Proudly local.
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-400">
              Based in West Chester and serving workplaces across the western
              Philadelphia suburbs — roughly a 20-mile radius. Tap your town:
            </p>
            <div className="mt-10 flex flex-wrap gap-2.5">
              {AREAS.map((a) => (
                <Link
                  key={a.slug}
                  to={TOWN_PAGE_SLUGS.has(a.slug) ? `/areas/${a.slug}` : "/service-area"}
                  data-testid={`home-area-chip-${a.slug}`}
                  className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[11px] tracking-[0.15em] uppercase text-slate-400 transition-all duration-300 hover:border-vortex-cyan/50 hover:text-vortex-cyan"
                >
                  <MapPin size={11} />
                  {a.name}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden" data-testid="cta-section">
        <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[30rem] w-[46rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-vortex-blue/15 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40">
          <Reveal>
            <h2 className="max-w-4xl font-heading text-4xl font-black tracking-tighter text-white sm:text-6xl lg:text-7xl">
              READY TO MODERNIZE YOUR <span className="text-vortex-cyan">BREAKROOM?</span>
            </h2>
            <div className="mt-12 flex flex-wrap gap-4">
              <button
                data-testid="cta-contact-button"
                onClick={() => navigate("/contact")}
                className="group flex items-center gap-3 rounded-full bg-vortex-cyan px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_50px_rgba(92,179,226,0.5)]"
              >
                Talk to us
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="cta-about-button"
                onClick={() => navigate("/about")}
                className="rounded-full border border-white/15 px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase text-slate-300 transition-all duration-300 hover:border-vortex-cyan/60 hover:text-vortex-cyan"
              >
                Our story
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
